import { removeEmptyKeys } from '@openeducation/pp-js-utils';
import {
  PaymentParams,
  PaymentMethod,
  BillingAddress,
  ManualPaymentParams,
  BillingAgreements,
  ExtendedPaymentInfoFormData,
} from '@openeducation/pp-rn-shared-types';
import type { PaymentGateway } from 'libs/pp-rn-shared-types/lib/typescript/src';

interface GetPaymentParams {
  paymentInfo: ExtendedPaymentInfoFormData;
  documentNumber?: string;
  documentType?: string;
  documentNumber2?: string;
  documentType2?: string;
  contractId?: string;
  isUpsell?: boolean;
  sfdcToken?: string;
  billingAddress?: BillingAddress;
  billingAddress2?: BillingAddress;
  manualPayment?: ManualPaymentParams;
  cpfNumber?: string;
  payPalToken?: string | null;
  usePaymentMethodOnFile?: boolean;
  billingAgreements?: BillingAgreements;
  alternativePaymentGateway?: PaymentGateway | null;
}

export const getPaymentParams = ({
  paymentInfo,
  contractId,
  documentNumber,
  documentType,
  documentNumber2,
  documentType2,
  isUpsell,
  sfdcToken,
  billingAddress,
  billingAddress2,
  manualPayment,
  cpfNumber,
  usePaymentMethodOnFile,
  billingAgreements,
  alternativePaymentGateway,
}: GetPaymentParams): PaymentParams => {
  const {
    paymentMethod,
    country,
    card1_expirationMonth,
    card1_expirationYear,
    card1_holderName,
    card1_cardType,
    card1_cardNumber,
    card1_cvv,
    card1_installments,
    card2_cardType,
    card2_cardNumber,
    card2_expirationMonth,
    card2_expirationYear,
    card2_holderName,
    card2_cvv,
    card2_installments,
  } = paymentInfo;

  const { baid: billingAgreementId, email: payPalEmailAddress } =
    (billingAgreements || {}) as BillingAgreements;
  const isUPM = !sfdcToken;

  const baseParams: Omit<
    PaymentParams,
    'creditCardInfo' | 'creditCardInfo2' | 'manualPayment'
  > = {
    ...(country ? { billingAddress: removeEmptyKeys(billingAddress) } : {}),
    ...(country && billingAddress2
      ? { billingAddress2: removeEmptyKeys(billingAddress2) }
      : {}),
    ...(contractId ? { contractId: contractId.toString() } : {}),
    ...(cpfNumber ? { cpfNumber } : {}),
    ...(documentNumber ? { documentNumber } : {}),
    ...(documentNumber2 ? { documentNumber2 } : {}),
    ...(documentType ? { documentType } : {}),
    ...(documentType2 ? { documentType2 } : {}),
    paymentMethod,
    ...(isUpsell ? { usePaymentMethodOnFile } : {}),
    ...(sfdcToken ? { paymentInfoToken: sfdcToken } : {}),
    ...(billingAgreements && isUPM
      ? { payPalBillingAgreement: billingAgreements }
      : {}),
    ...(!isUPM && billingAgreementId ? { billingAgreementId } : {}),
    ...(!isUPM && payPalEmailAddress
      ? { paypalEmailAddress: payPalEmailAddress }
      : {}),
    ...(alternativePaymentGateway ? { alternativePaymentGateway } : {}),
  };

  const paymentParamsPayload: PaymentParams = { ...baseParams };

  if (
    paymentMethod === PaymentMethod.CREDIT_CARD ||
    paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
  ) {
    paymentParamsPayload.creditCardInfo = {
      expirationMonth: parseInt(card1_expirationMonth, 10),
      expirationYear: parseInt(card1_expirationYear, 10) + 2000,
      holderName: card1_holderName,
      number: card1_cardNumber.replaceAll(' ', ''),
      type: card1_cardType,
      securityCode: card1_cvv,
      ...(card1_installments
        ? { installments: parseInt(card1_installments, 10) }
        : {}),
      ...(paymentInfo.card1_amount
        ? { amount: parseFloat(paymentInfo.card1_amount) }
        : {}),
    };

    if (paymentInfo.card1_documentNumber) {
      paymentParamsPayload.documentNumber = paymentInfo.card1_documentNumber;
    }
    if (paymentInfo.card1_documentType) {
      paymentParamsPayload.documentType = paymentInfo.card1_documentType;
    }
  }

  if (
    paymentMethod === PaymentMethod.TWO_CREDIT_CARDS &&
    card2_cardType &&
    card2_cardNumber &&
    card2_expirationMonth &&
    card2_expirationYear &&
    card2_cvv
  ) {
    paymentParamsPayload.creditCardInfo2 = {
      expirationMonth: parseInt(card2_expirationMonth, 10),
      expirationYear: parseInt(card2_expirationYear, 10) + 2000,
      holderName: card2_holderName,
      number: card2_cardNumber.replaceAll(' ', ''),
      type: card2_cardType,
      securityCode: card2_cvv,
      ...(card2_installments
        ? { installments: parseInt(card2_installments, 10) }
        : {}),
    };

    if (paymentInfo.card2_documentNumber) {
      paymentParamsPayload.documentNumber2 = paymentInfo.card2_documentNumber;
    }
    if (paymentInfo.card2_documentType) {
      paymentParamsPayload.documentType2 = paymentInfo.card2_documentType;
    }
  }

  if (paymentMethod === PaymentMethod.MANUAL && manualPayment) {
    paymentParamsPayload.manualPayment = manualPayment;
  }

  return paymentParamsPayload;
};
